import { useState, useEffect, useCallback, memo, useMemo } from "react";
import { fetchImageHistory, getEvalTasks, getProxiedDownloadUrl } from "../api";

import { SearchOutlined, InstagramOutlined, DownOutlined } from "@ant-design/icons/es";
import { Col, Row, Card, Input, Pagination, Tag, Switch, Button, Space, Dropdown, Menu, MenuProps, Checkbox } from "antd/es";

import CardTitle from "./CardTitle";

import '../style/mediabrowser.scss';
import { deepEquals, downloadIndividually, useInterval } from "../util";
import { Hoverable } from "./Hoverable";
import modelStorage from "../store/model-storage";
import { ArrowDownOutlined, CaretDownFilled, CheckSquareOutlined, DeleteOutlined, DownloadOutlined, DropboxSquareFilled, MenuOutlined, SaveFilled, SettingOutlined } from "@ant-design/icons";


const MediaBrowser = (props) => {
    const { onGenerated } = props;

    const [history, setHistory] = useState([]);
    const [totalMatches, setTotalMatches] = useState(0);
    const [query, setQuery] = useState('');
    const [collapsed, setCollapsed] = useState(false);
    const [page, setPage] = useState(1);
    const [evalQueue, setEvalQueue] = useState([]);

    const imageFilter = modelStorage.useImageFilter();
    const setImageFilter = modelStorage.useSetImageFilter();
    const [showEvals, setShowEvals] = useState(false);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);

    useEffect(() => {
        const listener = ({ imageFilter, evals }) => { setQuery(imageFilter); setShowEvals(evals); setPage(1); };
        window.addEventListener('set-image-filter', listener);
        return () => window.removeEventListener('set-image-filter', listener);
    }, []);

    const updateHistory = useCallback(async () => {
        const q = query.trim();

        let filters = { 'is_eval': showEvals ? true : null };
        if (imageFilter) {
            filters['model.keyword'] = imageFilter;
        }

        const { values, total } = await fetchImageHistory(36, filters, q, (page - 1) * 36);
        setHistory(values || []);
        setTotalMatches(total);
    }, [query, page, imageFilter, showEvals]);

    useInterval(() => updateHistory(), 10000, false, [updateHistory]);

    useInterval(() => {
        getEvalTasks().then(q => setEvalQueue(q.filter(e => !e.result)));
    }, 10000, false, [setEvalQueue]);


    const images = history.filter(h => h?.inference?.images)
        .map(h => ({ ...h.inference, '_timestamp': h['@timestamp'] }))
        .map(e => ({ args: e, url: e.images[0] }));

    const loadArgs = (args) => window.dispatchEvent(new CustomEvent('custom.setArgs', { detail: args }));
    const setBaseImage = (args) => window.dispatchEvent(new CustomEvent('custom.setBaseImage', { detail: args }));
    const setViewing = (args) => window.dispatchEvent(new CustomEvent('custom.setViewing', { detail: args }));

    const selectImage = (args, url) => {
        loadArgs(args);
        setViewing({ url, args });
    };

    useEffect(() => {
        onGenerated(() => () => updateHistory);
    }, [onGenerated, updateHistory]);

    const bodyStyle = { padding: '0 !important' };

    if (collapsed) {
        bodyStyle.display = 'none';
    }

    const menu = (
        <Menu>
            <Menu.Item>
                <Checkbox checked={selectionMode} onChange={ev => setSelectionMode(ev.target.checked)}>ahh zemmel</Checkbox>
            </Menu.Item>
        </Menu>
    );

    const pickImage = (img) => setSelectedImages(old => old.some(check => check.url === img.url) ? old.filter(e => e.url !== img.url) : [...old, img]);

    const addResultsToSelection = () => {
        setSelectedImages(old => {
            const newArr = [...old];

            for (const img of images) {
                if (!old.some(image => image.url === img.url)) {
                    newArr.push(img);
                }
            }

            return newArr;
        })
    }

    const downloadImages = () => {
        if (selectedImages.length >= 100 && !window.confirm("Dat zijn er echt kanker veel... hou je vast bertje!!!!")) {
            return;
        }

        downloadIndividually(selectedImages);
    };

    return (
        <Card bodyStyle={bodyStyle} title={<CardTitle icon={<InstagramOutlined />} collapsed={collapsed} setCollapsed={setCollapsed}>Media Browser <span className="subtitle">{evalQueue.length} eval{evalQueue.length === 1 ? '' : 's'} queued</span></CardTitle>} className="run-container settings-card" style={{ height: '100%' }}>
            <Row>
                <Col span={24}>
                    <Input
                        style={{ marginBottom: 8 }}
                        placeholder="Search..."
                        suffix={<>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{totalMatches} results</span>
                            <Button type="text" size="small" icon={<DeleteOutlined />} style={{ height: 20 }} onClickCapture={e => { e.preventDefault(); e.stopPropagation(); setPage(1); setShowEvals(false); setQuery(''); }} />
                        </>}
                        value={query}
                        onChange={e => { setQuery(e.target.value); setPage(1); }}
                        addonBefore={<SearchOutlined style={{ padding: '0 8px' }} />}
                        addonAfter={
                            <Dropdown overlay={menu} trigger={['click']}>
                                <Button icon={<MenuOutlined />} type={selectionMode ? "primary" : "default"} />
                            </Dropdown>
                        }
                    // addonAfter={<Switch checked={showEvals} onChange={e => setShowEvals(e)} title="Show evals" />}
                    />

                    {imageFilter && <div className="image-browser-model-filter">
                        <Tag color="blue" closable onClose={() => setImageFilter(null)}>{imageFilter}</Tag>
                    </div>}
                </Col>
            </Row>

            {selectionMode && <Row>
                <Col span={24}>
                    <span>
                        <Tag>
                            <span>Current selection: {selectedImages.length}</span>
                            <Button size="small" type="link" danger onClick={() => setSelectedImages([])} icon={null}><DeleteOutlined />clear</Button>
                        </Tag>

                        <Button size="small" onClick={() => addResultsToSelection()}>Add current page to selection</Button>
                    </span>

                    <span style={{ float: 'right' }}>
                        <Button type="text" size="small" onClick={() => setSelectionMode(false)}>cancel</Button>
                        <Button type={selectedImages.length ? "success" : "text"} size="small" icon={<ArrowDownOutlined />} disabled={!selectedImages.length} onClick={downloadImages}>
                            Save {selectedImages.length} images
                        </Button>
                    </span>
                </Col>
            </Row>}

            <Row>
                <Col span={24} style={{ textAlign: 'center', marginBottom: 8 }}>
                    <Pagination
                        position="bottom"
                        total={totalMatches}
                        current={page}
                        pageSize={36}
                        onChange={setPage}
                        showQuickJumper={false}
                        showSizeChanger={false}
                        showTotal={false}
                        align="center"
                    />
                </Col>
            </Row>

            <Row gutter={0} >
                <ImageThumbGrid
                    images={images}
                    selectImage={selectImage}
                    setBaseImage={setBaseImage}
                    selectionMode={selectionMode}
                    pickImage={pickImage}
                    selectedImages={selectedImages}
                />
            </Row>

            <Row>
                <Col span={24} style={{ textAlign: 'center', marginTop: 8 }}>
                    <Pagination
                        position="bottom"
                        total={totalMatches}
                        current={page}
                        pageSize={36}
                        onChange={setPage}
                        showQuickJumper={false}
                        showSizeChanger={false}
                        showTotal={false}
                        align="center"
                    />
                </Col>
            </Row>
        </Card>
    )
}

export const ImageThumbGrid = memo(function ImageThumbGrid(props) {
    const { images, selectImage, setBaseImage, selectionMode, pickImage, selectedImages, getThumbnail, getTopRightInfo } = props;

    return (<>
        {images.map(({ args, url }, idx) => (
            <ImageThumb
                key={args._timestamp}
                args={args}
                image={url}
                onClick={() => selectImage(args, url, idx)}
                setBaseImage={setBaseImage}
                newSeed={idx < images.length - 1 && args.seed !== images[idx + 1].args.seed + 1}
                selectionMode={selectionMode}
                selectImage={pickImage}
                isSelected={selectedImages.some(img => img.url === url)}
                getThumbnail={getThumbnail}
                getTopRightInfo={getTopRightInfo ? (args) => getTopRightInfo(args, idx) : null}
            />
        ))}
    </>)
}, deepEquals);


const SCHEDULER_NAME_MAP = {
    'eulera': 'EA',
    'euler': 'e',
    'dpm3': 'D3',
    'unipc': 'PC',
}

function replaceLast(str, search, replacement) {
    const lastIndex = str.lastIndexOf(search);

    if (lastIndex === -1) {
        return str;
    }

    return str.slice(0, lastIndex) + replacement + str.slice(lastIndex + search.length);
}

const ImageThumb = memo(function ImageThumb(props) {
    const { image, args, onClick, setBaseImage, newSeed, selectionMode, selectImage, isSelected, getThumbnail, getTopRightInfo } = props;

    const onContextMenu = useCallback(e => {
        e.preventDefault();
        setBaseImage(image);
        return false;
    }, [image, setBaseImage]);

    let url = image;
    let thumb = image;

    const isSmall = (args.width ?? 1) * (args.height ?? 1) <= (512 * 512);

    if (args.has_thumbs) {
        url = replaceLast(image, '.png', isSmall ? '.webp' : '--hq.webp').replace('.png_', '_');
        thumb = replaceLast(image, '.png', isSmall ? '.webp' : '--thumb256.webp').replace('.png_', '_');
    } else if (getThumbnail) {
        thumb = getThumbnail(image);
    }

    const scheduler = SCHEDULER_NAME_MAP[args.scheduler] ?? args.scheduler;
    const hoverComponent = useMemo(() => <HoverPreview image={url} args={args} />, [args, url]);

    const onClickF = () => {
        if (selectionMode) {
            selectImage({ url: image, args });
        } else {
            onClick();
        }
    }

    let classes = "";
    if (selectionMode && isSelected) {
        classes = "selected";
    }

    let topRight = null;
    if (getTopRightInfo) {
        topRight = getTopRightInfo(image);
    }

    if (!topRight) {
        topRight = (typeof (args.cfg) === 'number') && <>{args.cfg.toFixed(1)}</>;
    }

    return (
        <Hoverable component={selectionMode ? null : hoverComponent}>
            <Col xxl={4} xs={6} className={`inference-image-thumb-wrapper ${newSeed ? 'new-seed' : ''} ${classes}`} onContextMenu={onContextMenu}>
                <img draggable={false} className="inference-image-thumb" src={thumb} width="100%" onClick={onClickF} />

                <span className="inference-image-thumb-footer">
                    <span className="top left">{scheduler} {args.steps}</span>
                    <span className="top right">{topRight}</span>
                    {(typeof (args.seed) === 'number') && <span className="bottom right tight" style={{ fontFamily: 'monospace' }}>{args.seed.toString(36)}</span>}
                </span>
            </Col>
        </Hoverable>
    );
}, deepEquals);

const filtered = ['images', 'num', 'subdir', 'prompt', 'name', 'guidance_rescale', 'zero_pooled', 'zero_unpooled', 'model', 'key', 'strength', 'stretch_images'];

const HoverPreview = (props) => {
    const { image, args } = props;

    const imageRendering = (args.width < 512 || args.height) < 512 ? 'pixelated' : 'initial';

    return (<div className="image-preview-container">
        <div className="image-preview-main">
            {args.flux2 && <>
                <span style={{ position: 'absolute', top: 12, left: 12, border: '1px solid var(--accent-primary)', color: 'var(--accent-primary)', fontSize: 11, padding: '2px 4px', borderRadius: 8, background: 'color-mix(in srgb, var(--accent-primary), transparent 90%)' }}>Flux.2</span>
            </>}
            <img src={image} style={{ maxWidth: 384, maxHeight: 384, imageRendering, aspectRatio: `${args.width || 1}/${args.height || 1}` }} />

            <div className="image-preview-footer">
                {args.prompt}
            </div>
        </div>

        <ParameterList args={args} />
    </div>);
}

export const ParameterList = (props) => {
    const { args } = props;

    return (
        <div className="image-preview-params">
            {Object.keys(args).filter(k => !filtered.includes(k)).sort().map(k => (
                <Parameter key={k} name={k} value={args[k]} />
            ))}
        </div>
    )
}

const Parameter = (props) => {
    const { name, value } = props;
    if (name === "loras" && value) {
        return <LoraParameter name={name} value={value} />
    }

    return (
        <div className="param-row">
            <span className="param-name">{name}</span>
            <span className="param-value">{(value ?? "").toString()}</span>
        </div>
    )
}

const LoraParameter = (props) => {
    const { name, value } = props;
    return (
        <div className="param-row">
            <span className="param-name">{name}</span>
            <span className="param-value">{value?.filter(l => l).map(l => l.path)}</span>
        </div>
    )
}


export default MediaBrowser;